/**
 * Survey report generation utilities for the admin portal.
 *
 * Produces a self-contained HTML report for a single survey: a distribution
 * bar chart per Likert question plus every free-text comment, listed
 * unattributed (no member name or email).
 */

import type { Survey, SurveyResults } from '@/services/api/surveys'

const LIKERT_LABELS: Record<number, string> = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree',
}

/**
 * Escape a value for safe inclusion in HTML text or a double-quoted attribute.
 */
function escapeHtml(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Render the 5-row distribution bar chart for a single Likert question.
 *
 * Mirrors the count/percentage/bar-width math used by the on-screen
 * SurveyResults component.
 */
function renderDistribution(distribution: Record<number, number>, totalResponses: number): string {
  const maxCount = Math.max(...Object.values(distribution), 1)

  const rows = [1, 2, 3, 4, 5]
    .map((rating) => {
      const count = distribution[rating] ?? 0
      const percentage = totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0
      const barWidth = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0

      return `
        <div class="bar-row">
          <span class="bar-label">${escapeHtml(LIKERT_LABELS[rating])}</span>
          <span class="bar-track"><span class="bar-fill" style="width: ${barWidth}%"></span></span>
          <span class="bar-count">${count} (${percentage}%)</span>
        </div>`
    })
    .join('')

  return `<div class="distribution">${rows}</div>`
}

/**
 * Build a full, self-contained HTML document reporting the results of a survey.
 *
 * Comments are listed unattributed: no user name, email, or id is read.
 */
export function generateSurveyReportHtml(survey: Survey, results: SurveyResults): string {
  const generatedOn = new Date().toLocaleString()

  const questions = results.likert_results
    .map(
      (question) => `
      <section class="question">
        <h2>${escapeHtml(question.question_text)}</h2>
        <p class="average">Average: ${question.average.toFixed(2)}</p>
        ${renderDistribution(question.distribution, results.total_responses)}
      </section>`,
    )
    .join('')

  const comments = results.individual_responses
    .map((response) => response.free_text_answer?.trim())
    .filter((answer): answer is string => Boolean(answer))
    .map((answer) => `<li>${escapeHtml(answer)}</li>`)
    .join('')

  const commentsSection = `
    <section class="comments">
      <h2>Comments</h2>
      <p class="prompt">${escapeHtml(survey.free_text_question)}</p>
      ${comments ? `<ul>${comments}</ul>` : '<p class="empty">No comments were submitted.</p>'}
    </section>`

  const description = survey.description
    ? `<p class="description">${escapeHtml(survey.description)}</p>`
    : ''

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(survey.title)} — Survey Report</title>
  <style>
    :root { color-scheme: light; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #1a1a1a;
      max-width: 800px;
      margin: 2rem auto;
      padding: 0 1.5rem;
      line-height: 1.5;
    }
    h1 { font-size: 1.6rem; margin-bottom: 0.25rem; }
    h2 { font-size: 1.1rem; margin: 0 0 0.5rem; }
    .description { color: #555; }
    .meta { color: #666; font-size: 0.85rem; margin-bottom: 2rem; }
    .question { margin-bottom: 2rem; page-break-inside: avoid; }
    .average { color: #666; font-size: 0.85rem; margin: 0 0 0.75rem; }
    .distribution { display: flex; flex-direction: column; gap: 0.4rem; }
    .bar-row { display: flex; align-items: center; gap: 0.75rem; font-size: 0.85rem; }
    .bar-label { width: 9rem; flex-shrink: 0; color: #555; }
    .bar-track { flex: 1; background: #ececec; border-radius: 9999px; height: 1rem; overflow: hidden; }
    .bar-fill { display: block; height: 100%; background: #4f46e5; border-radius: 9999px; }
    .bar-count { width: 4rem; text-align: right; flex-shrink: 0; }
    .comments { margin-top: 2.5rem; page-break-inside: avoid; }
    .comments .prompt { color: #555; font-style: italic; }
    .comments ul { padding-left: 1.25rem; }
    .comments li { margin-bottom: 0.5rem; }
    .comments .empty { color: #666; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <header>
    <h1>${escapeHtml(survey.title)}</h1>
    ${description}
    <p class="meta">
      ${results.total_responses} response${results.total_responses === 1 ? '' : 's'}
      &middot; Generated ${escapeHtml(generatedOn)}
    </p>
  </header>
  ${questions}
  ${commentsSection}
</body>
</html>`
}

/**
 * Generate a datestamped filename for the survey report.
 */
function buildFilename(survey: Survey): string {
  const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
  const slug = survey.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `survey-report-${slug ? `${slug}-` : ''}${today}.html`
}

/**
 * Build the report HTML and trigger a browser download of it as a file.
 *
 * Creates a temporary Blob URL, clicks it, then revokes it.
 */
export function downloadSurveyReport(survey: Survey, results: SurveyResults): void {
  const html = generateSurveyReportHtml(survey, results)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = buildFilename(survey)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
